import { useEffect, useState } from "react";
import type { CustomApp } from "@clickdialog/product-registry";
import type { PlatformApiClient } from "./api";
import { AdaptiveGrid, DetailsList, Page, PageHeader, StatusState } from "./adaptive/primitives";
import { customAppIcon, renderCustomAppRuntime } from "./custom-app-runtime";
import type { OrganizationContext } from "./types";

function appKeyFromPath(nestedPath: string): string | null {
  const key = nestedPath.split("/").filter(Boolean)[0] ?? null;
  return key && /^[a-z0-9](?:[a-z0-9-]{0,62})$/.test(key) ? key : null;
}

export function CustomProduct({
  organization,
  nestedPath,
  api,
  navigate,
}: {
  organization: OrganizationContext;
  nestedPath: string;
  api: PlatformApiClient;
  navigate: (path: string) => void;
}) {
  const appKey = appKeyFromPath(nestedPath);
  const [apps, setApps] = useState<readonly CustomApp[]>([]);
  const [app, setApp] = useState<CustomApp | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setState("loading");
    setError("");
    const request = appKey
      ? api.resolveCustomApp(organization.organization.id, appKey).then((result) => {
          if (!cancelled) setApp(result.customApp);
        })
      : api.listCustomApps(organization.organization.id).then((result) => {
          if (!cancelled) setApps(result.customApps);
        });
    request
      .then(() => { if (!cancelled) setState("ready"); })
      .catch((cause) => {
        if (!cancelled) {
          setState("error");
          setError(cause instanceof Error ? cause.message : "No se pudo cargar la aplicación personalizada.");
        }
      });
    return () => { cancelled = true; };
  }, [api, appKey, organization.organization.id]);

  if (state === "loading") {
    return <StatusState className="product-boundary" tone="loading" eyebrow="Aplicaciones personalizadas" title="Cargando Custom Apps…" message="Estamos preparando las aplicaciones de este negocio." />;
  }

  if (state === "error") {
    return <StatusState className="product-boundary unavailable" tone="danger" live="assertive" eyebrow="Aplicaciones personalizadas" title="No se pudieron cargar las aplicaciones" message={error} />;
  }

  if (appKey && app) {
    const customRuntime = renderCustomAppRuntime(app, {
      organization,
      api,
      onAppChange: setApp,
    });
    if (customRuntime !== null) return customRuntime;

    return <Page>
      <PageHeader eyebrow="Aplicación personalizada" title={app.displayName} description={`Aplicación hecha a medida para ${organization.organization.displayName}.`} />
      <section className="product-boundary available">
        <span className="boundary-label">Custom App</span>
        <h2>{app.displayName}</h2>
        <DetailsList items={[
          { key: "key", term: "Clave", detail: app.appKey },
          { key: "status", term: "Estado", detail: app.status },
          { key: "version", term: "Versión", detail: app.version },
        ]} />
      </section>
    </Page>;
  }

  return <Page>
    <PageHeader eyebrow="Aplicaciones personalizadas" title="Custom Apps" description="Software hecho a medida, administrado desde la misma cuenta de ClickDialog." />
    {apps.length === 0
      ? <StatusState className="product-boundary" title="Sin aplicaciones" message="Esta organización todavía no tiene una aplicación personalizada activa." />
      : <AdaptiveGrid className="custom-app-grid" compact={1} medium={2} wide={2}>
        {apps.map((item) => <button
          className="custom-app-card"
          key={item.id}
          onClick={() => navigate(`/o/${organization.organization.slug}/custom/${item.appKey}`)}
        >
          <span className="custom-app-icon">{customAppIcon(item.appKey)}</span>
          <span><strong>{item.displayName}</strong><small>{item.status === "active" ? "Activa" : item.status}</small></span>
          <span aria-hidden="true">→</span>
        </button>)}
      </AdaptiveGrid>}
  </Page>;
}
